import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Spin, message, Button, Space } from 'antd';
import { InventoryLabelData } from '../types/inventoryLabel';
import { inventoryLabelService } from '../services/inventoryLabelService';

interface InventoryLabelPreviewProps {
  visible: boolean;
  data?: InventoryLabelData;
  onClose: () => void;
  /** 为 true 时在底部显示「打印」「关闭」，默认 false，保持列表页等现有行为不变 */
  showPrintFooter?: boolean;
  /** 嵌套在其他 Modal 之上时使用，例如扫描入库「扫描结果」弹窗内打开预览 */
  zIndex?: number;
}

export const InventoryLabelPreview: React.FC<InventoryLabelPreviewProps> = ({
  visible,
  data,
  onClose,
  showPrintFooter = false,
  zIndex,
}) => {
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [isPDF, setIsPDF] = useState(false);
  const pdfIframeRef = useRef<HTMLIFrameElement>(null);
  const previewUrlRef = useRef<string>('');

  useEffect(() => {
    let cancelled = false;
    const revoke = (url: string) => {
      if (url && url.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(url);
        } catch {}
      }
    };

    if (!visible || !data) {
      const u = previewUrlRef.current;
      previewUrlRef.current = '';
      setPreviewUrl('');
      setIsPDF(false);
      if (u) revoke(u);
      return;
    }

    setLoading(true);
    void (async () => {
      try {
        const response = await inventoryLabelService.generatePreview(data);
        if (cancelled) {
          revoke(response.url);
          return;
        }
        const prev = previewUrlRef.current;
        if (prev) revoke(prev);
        previewUrlRef.current = response.url;
        setPreviewUrl(response.url);
        setIsPDF(response.isPDF);
      } catch (error) {
        if (!cancelled) {
          console.error('预览生成失败:', error);
          message.error('标签预览失败');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      const u = previewUrlRef.current;
      previewUrlRef.current = '';
      revoke(u);
      setPreviewUrl('');
      setIsPDF(false);
    };
  }, [data, visible]);

  const handleClose = () => {
    const u = previewUrlRef.current;
    previewUrlRef.current = '';
    if (u) {
      try {
        if (u.startsWith('blob:')) URL.revokeObjectURL(u);
      } catch {}
    }
    setPreviewUrl('');
    setIsPDF(false);
    onClose();
  };

  const handlePrint = useCallback(() => {
    if (!previewUrl) {
      message.warning('暂无可打印内容');
      return;
    }
    if (isPDF) {
      try {
        const win = pdfIframeRef.current?.contentWindow;
        if (win) {
          win.focus();
          win.print();
          return;
        }
      } catch {
        message.warning('打印失败，请尝试下载后打印');
        return;
      }
      message.warning('预览未就绪，请稍候再试');
      return;
    }
    try {
      const w = window.open('', '_blank');
      if (!w) {
        message.warning('请允许弹窗后重试打印');
        return;
      }
      const safe = previewUrl.replace(/"/g, '&quot;');
      w.document.write(
        `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>库存标签打印</title></head><body style="margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh">` +
          `<img src="${safe}" alt="" style="max-width:100%;max-height:100vh;object-fit:contain" onload="try{window.focus();window.print();}catch(e){}" />` +
          `</body></html>`
      );
      w.document.close();
    } catch {
      message.warning('打印失败，请稍后重试');
    }
  }, [previewUrl, isPDF]);

  return (
    <Modal
      open={visible}
      title="库存标签预览"
      footer={
        showPrintFooter ? (
          <Space>
            <Button type="primary" disabled={!previewUrl || loading} onClick={() => handlePrint()}>
              打印
            </Button>
            <Button onClick={handleClose}>关闭</Button>
          </Space>
        ) : null
      }
      onCancel={handleClose}
      width={800}
      destroyOnClose
      zIndex={zIndex}
    >
      {loading ? (
        <div className="flex justify-center items-center py-8">
          <Spin size="large" />
        </div>
      ) : previewUrl && (
        <div className="flex justify-center">
          {isPDF ? (
            <iframe
              ref={pdfIframeRef}
              src={previewUrl}
              title="库存标签预览"
              style={{ width: '100%', height: '500px' }}
              className="shadow-lg rounded"
            />
          ) : (
            <img
              src={previewUrl}
              alt="库存标签预览"
              style={{ maxWidth: '100%' }}
              className="shadow-lg rounded"
            />
          )}
        </div>
      )}
    </Modal>
  );
}; 